import React from "react";
import { FeedFollowCtaWithDetails } from "web/react/components/feed-follow-cta-with-details";
import { PromotionHeader } from "web/react/components/promotion-header";
import { StorefrontDescription } from "web/react/components/storefront-description/";
import { StorefrontInfoList } from "web/react/components/storefront-modal/storefront-info-list/";
import { HStack } from "web/react/emo/h-stack";
import { SellerLogoCard } from "web/react/emo/seller-logo-card/seller-logo-card";
import { Sticker } from "web/react/emo/sticker";
import { TertiaryArticleCard } from "web/react/emo/tertiary-article-card";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { gettext } from "web/script/modules/django-i18n";
import { canUseMembership } from "web/script/modules/utils";
import {
    ArticleCardSerializer,
    FollowButtonSerializer,
    RetailerPromotionsSerializer,
} from "web/types/serializers";

export interface StorefrontHeaderSummaryProps {
    retailerName: string;
    description: string | null;
    photoUrl: string | null;
    smallPhotoUrl: string | null;
    followButton: FollowButtonSerializer | null;
    followersCountLabel: string | null;
    dateAdded: string | null;
    shipsToCountry: string | null;
    bestShippingOption: string | null;
    promotions?: RetailerPromotionsSerializer | null;
    isCheckout?: boolean;
    isOfficialStore?: boolean;
    onClick: () => void;
    articles?: ArticleCardSerializer[] | null;
    parentArea: "header" | "modal" | "product_details" | "compare_prices";
    retailerNetwork: string | null;
}

export function StorefrontHeaderSummary({
    retailerName,
    description,
    photoUrl,
    smallPhotoUrl,
    followButton,
    followersCountLabel,
    promotions,
    dateAdded,
    shipsToCountry,
    bestShippingOption,
    isCheckout = false,
    isOfficialStore = false,
    onClick,
    articles = null,
    retailerNetwork,
}: StorefrontHeaderSummaryProps): React.ReactElement {
    return (
        <VStack spacing="md">
            <VStack spacing="sm">
                <SellerLogoCard
                    imageURL={(photoUrl || smallPhotoUrl) ?? ""}
                    sellerName={retailerName}
                    isCheckout={isCheckout}
                    size="md"
                >
                    <div>
                        {canUseMembership() && (
                            <FeedFollowCtaWithDetails
                                followButton={followButton}
                                followersCountLabel={followersCountLabel}
                                designerName={retailerName}
                            />
                        )}
                    </div>
                </SellerLogoCard>
                {(isCheckout || isOfficialStore) && (
                    <HStack spacing="xxs">
                        {isCheckout && <Sticker name="lystShopPartner" />}
                        {isOfficialStore && <Sticker name="officialStore" />}
                    </HStack>
                )}
                <StorefrontDescription
                    description={description}
                    parentArea="modal"
                    retailerNetwork={retailerNetwork}
                />
                {!!promotions && (
                    <PromotionHeader
                        promotions={promotions}
                        title={retailerName}
                        parentArea="modal"
                        onClick={onClick}
                        retailerNetwork={retailerNetwork}
                    />
                )}
            </VStack>
            <VStack spacing="sm">
                <StorefrontInfoList
                    dateAdded={dateAdded}
                    shipsToCountry={shipsToCountry}
                    bestShippingOption={bestShippingOption}
                />
                {isCheckout && (
                    <Text textStyle="body-xs" color="secondary">
                        {gettext("storefront.verified.disclaimer")}
                    </Text>
                )}
            </VStack>
            {articles && articles.length > 0 && (
                <View dataTestId="storefront-articles">
                    <View marginBottom="sm">
                        <Text textStyle="body-bold">{gettext("storefront.articles.title")}</Text>
                    </View>
                    <VStack spacing="xxs">
                        {articles.map((article, i) => (
                            <TertiaryArticleCard
                                key={`${article.slug}-${i}-pdp`}
                                // TODO BEAM-1111 https://lyst.atlassian.net/browse/BEAM-1111
                                // send onClick analytics event
                                onClick={() => {}}
                                {...article}
                                hasBackground={false}
                                variant="lg"
                            />
                        ))}
                    </VStack>
                </View>
            )}
        </VStack>
    );
}
